import React, { useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Col,
  Descriptions,
  Drawer,
  Empty,
  Form,
  Input,
  Modal,
  Popconfirm,
  Row,
  Space,
  Statistic,
  Table,
  Tag,
  Typography,
  message,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import {
  CopyOutlined,
  DeleteOutlined,
  PlusOutlined,
  ReloadOutlined,
  TeamOutlined,
  DollarOutlined,
  RiseOutlined,
  LinkOutlined,
} from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { agentCenterApi } from '../services/api';
import { formatBeijingTime } from '../utils/dateUtils';

const { Title, Text } = Typography;

const formatUsdt = (value?: number | null) => `${(Number(value) || 0).toFixed(2)} U`;

interface AgentUser {
  id: number;
  telegram_id: number;
  username?: string | null;
  first_name?: string | null;
  last_name?: string | null;
  is_admin: boolean;
  is_agent: boolean;
  agent_enabled: boolean;
  agent_code?: string | null;
  agent_available_balance: number;
  available_balance: number;
  total_earnings: number;
  join_date?: string | null;
}

interface AgentListItem {
  agent: AgentUser;
  member_count: number;
  today_turnover: number;
  today_commission: number;
}

interface AgentStat {
  stat_date: string;
  turnover_amount: number;
  order_count: number;
  commission_rate: number;
  commission_amount: number;
}

interface AgentMemberItem {
  relation_id: number;
  bound_at?: string | null;
  member?: AgentUser | null;
}

interface AgentTurnoverItem {
  relation_id: number;
  bound_at?: string | null;
  member?: AgentUser | null;
  today_turnover: number;
}

interface AgentTurnoverPage {
  items: AgentTurnoverItem[];
  page: number;
  page_size: number;
  total: number;
  total_pages: number;
  today?: string | null;
}

interface AgentDetail {
  agent: AgentUser;
  today_stat?: AgentStat | null;
  recent_stats: AgentStat[];
  member_count: number;
  lifetime_commission: number;
  merchant_lifetime_earnings: number;
  agent_available_balance: number;
  agent_withdrawable_balance: number;
  agent_pending_balance: number;
  merchant_available_balance: number;
  lifetime_turnover: number;
  recent_members: AgentMemberItem[];
  turnover_page: AgentTurnoverPage;
  invite_link?: string | null;
  today?: string | null;
}

const formatUserName = (user?: AgentUser | null) => {
  if (!user) return '-';
  if (user.username) return `@${user.username}`;
  const name = [user.first_name, user.last_name].filter(Boolean).join(' ');
  return name || `用户#${user.id}`;
};

const formatDateText = (value?: string | null) => {
  if (!value) return '-';
  return value.replace(/-/g, '/');
};

const AgentCenter: React.FC = () => {
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [createVisible, setCreateVisible] = useState(false);
  const [selectedAgentId, setSelectedAgentId] = useState<number | null>(null);
  const [detailTurnoverPage, setDetailTurnoverPage] = useState(1);
  const [detailTurnoverPageSize, setDetailTurnoverPageSize] = useState(10);
  const [createForm] = Form.useForm<{ identifier: string }>();

  const { data: overview, isLoading, refetch } = useQuery(
    ['agent-center-overview', page, pageSize],
    async () => (await agentCenterApi.getOverview({ page, page_size: pageSize })) as any,
    {
      keepPreviousData: true,
    }
  );

  const { data: agentDetail, isFetching: detailLoading } = useQuery(
    ['agent-center-detail', selectedAgentId, detailTurnoverPage, detailTurnoverPageSize],
    async () => (
      await agentCenterApi.getDetail(selectedAgentId as number, {
        turnover_page: detailTurnoverPage,
        turnover_page_size: detailTurnoverPageSize,
      })
    ) as unknown as AgentDetail,
    {
      enabled: selectedAgentId !== null,
      keepPreviousData: true,
    }
  );

  const openAgentDetail = (agentUserId: number) => {
    setDetailTurnoverPage(1);
    setDetailTurnoverPageSize(10);
    setSelectedAgentId(agentUserId);
  };

  const closeAgentDetail = () => {
    setSelectedAgentId(null);
    setDetailTurnoverPage(1);
    setDetailTurnoverPageSize(10);
  };

  const createAgentMutation = useMutation(
    async (identifier: string) => (await agentCenterApi.createAgent({ identifier })) as unknown as AgentDetail,
    {
    onSuccess: (data: AgentDetail) => {
      message.success('代理已添加');
      setCreateVisible(false);
      createForm.resetFields();
      openAgentDetail(data.agent.id);
      queryClient.invalidateQueries(['agent-center-overview']);
      queryClient.invalidateQueries(['agent-center-detail', data.agent.id]);
    },
    onError: (error: any) => {
      message.error(error?.response?.data?.detail || '添加代理失败');
    },
  });

  const deleteAgentMutation = useMutation(
    async (agentUserId: number) => agentCenterApi.deleteAgent(agentUserId),
    {
    onSuccess: (_: any, agentUserId: number) => {
      message.success('代理已删除');
      if (selectedAgentId === agentUserId) {
        setSelectedAgentId(null);
      }
      queryClient.invalidateQueries(['agent-center-overview']);
    },
    onError: (error: any) => {
      message.error(error?.response?.data?.detail || '删除代理失败');
    },
  });

  const handleCreateAgent = async () => {
    const values = await createForm.validateFields();
    createAgentMutation.mutate(values.identifier.trim());
  };

  const handleCopy = async (value?: string | null) => {
    if (!value) {
      message.warning('暂无可复制内容');
      return;
    }
    try {
      await navigator.clipboard.writeText(value);
      message.success('已复制');
    } catch (error) {
      message.error('复制失败，请手动复制');
    }
  };

  const agentItems: AgentListItem[] = overview?.agent_page?.items || [];
  const pagination = overview?.agent_page;

  const columns: ColumnsType<AgentListItem> = useMemo(() => ([
    {
      title: '代理',
      key: 'agent',
      render: (_, record) => (
        <Space direction="vertical" size={0}>
          <Space>
            <Text strong>{formatUserName(record.agent)}</Text>
            {record.agent.is_admin && <Tag color="blue">管理员</Tag>}
          </Space>
          <Text type="secondary">邀请码：{record.agent.agent_code || '-'}</Text>
        </Space>
      ),
    },
    {
      title: '用户标识',
      key: 'ids',
      width: 220,
      render: (_, record) => (
        <Space direction="vertical" size={0}>
          <Text>ID：{record.agent.id}</Text>
          <Text type="secondary">TG：{record.agent.telegram_id}</Text>
        </Space>
      ),
    },
    {
      title: '下级数',
      dataIndex: 'member_count',
      key: 'member_count',
      width: 100,
      render: (value: number) => value || 0,
    },
    {
      title: '今日流水',
      dataIndex: 'today_turnover',
      key: 'today_turnover',
      width: 120,
      render: (value: number) => formatUsdt(value),
    },
    {
      title: '今日佣金',
      dataIndex: 'today_commission',
      key: 'today_commission',
      width: 120,
      render: (value: number) => formatUsdt(value),
    },
    {
      title: '账户余额',
      key: 'available_balance',
      width: 120,
      render: (_, record) => formatUsdt(record.agent.available_balance),
    },
    {
      title: '操作',
      key: 'actions',
      width: 180,
      render: (_, record) => (
        <Space>
          <Button size="small" onClick={() => openAgentDetail(record.agent.id)}>
            详情
          </Button>
          <Popconfirm
            title="确认删除该代理？"
            description="这里是逻辑删除，会停用该代理，不会清历史数据。"
            okText="删除"
            cancelText="取消"
            okButtonProps={{ danger: true }}
            onConfirm={() => deleteAgentMutation.mutate(record.agent.id)}
          >
            <Button
              size="small"
              danger
              icon={<DeleteOutlined />}
              loading={deleteAgentMutation.isLoading}
            >
              删除代理
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ]), [deleteAgentMutation.isLoading]);

  const recentStatColumns: ColumnsType<AgentStat> = [
    {
      title: '日期',
      dataIndex: 'stat_date',
      key: 'stat_date',
      width: 120,
      render: (value: string) => formatDateText(value),
    },
    {
      title: '日流水',
      dataIndex: 'turnover_amount',
      key: 'turnover_amount',
      width: 120,
      render: (value: number) => formatUsdt(value),
    },
    {
      title: '佣金',
      dataIndex: 'commission_amount',
      key: 'commission_amount',
      width: 120,
      render: (value: number) => formatUsdt(value),
    },
    {
      title: '点位',
      dataIndex: 'commission_rate',
      key: 'commission_rate',
      width: 100,
      render: (value: number) => `${((value || 0) * 100).toFixed(0)}%`,
    },
    {
      title: '订单数',
      dataIndex: 'order_count',
      key: 'order_count',
      width: 100,
      render: (value: number) => value || 0,
    },
  ];

  const recentMemberColumns: ColumnsType<AgentMemberItem> = useMemo(() => ([
    {
      title: '商户账号',
      key: 'member',
      render: (_, record) => formatUserName(record.member),
    },
    {
      title: '用户ID',
      key: 'member_id',
      width: 120,
      render: (_, record) => record.member?.id || '-',
    },
    {
      title: 'Telegram ID',
      key: 'telegram_id',
      width: 150,
      render: (_, record) => record.member?.telegram_id || '-',
    },
    {
      title: '绑定时间',
      dataIndex: 'bound_at',
      key: 'bound_at',
      width: 180,
      render: (value: string) => formatBeijingTime(value),
    },
  ]), []);

  const turnoverDetailColumns: ColumnsType<AgentTurnoverItem> = [
    {
      title: '被代理商户名称',
      key: 'member',
      render: (_, record) => formatUserName(record.member),
    },
    {
      title: '被代理商户今日流水总金额',
      dataIndex: 'today_turnover',
      key: 'today_turnover',
      width: 220,
      align: 'right',
      render: (value: number) => formatUsdt(value),
    },
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <Title level={2} style={{ marginBottom: 8 }}>
            代理中心
          </Title>
          <Text type="secondary">
            网页端统一管理代理数据，仅统计一级代理及其下级商户。代理佣金会先进入待入账，24 小时后计入可提现余额。
          </Text>
        </div>

        <Space>
          <Button icon={<ReloadOutlined />} onClick={() => refetch()} loading={isLoading}>
            刷新
          </Button>
          <Button type="primary" icon={<PlusOutlined />} onClick={() => setCreateVisible(true)}>
            添加代理
          </Button>
        </Space>
      </div>

      <Alert
        type="info"
        showIcon
        message="支持按用户 ID、Telegram ID 开通代理。下级商户仅能通过邀请链接归属代理，已有商户不可重新绑定；删除代理为逻辑停用，不会清除历史佣金与订单归属。"
      />

      <Row gutter={[16, 16]}>
        <Col xs={24} sm={12} xl={6}>
          <Card className="stats-card">
            <Statistic title="代理总数" value={overview?.total_agents || 0} prefix={<TeamOutlined />} />
          </Card>
        </Col>
        <Col xs={24} sm={12} xl={6}>
          <Card className="stats-card">
            <Statistic title="今日新增商户" value={overview?.today_new_bindings || 0} prefix={<LinkOutlined />} />
          </Card>
        </Col>
        <Col xs={24} sm={12} xl={6}>
          <Card className="stats-card">
            <Statistic title="今日代理流水" value={overview?.today_turnover || 0} precision={2} prefix={<RiseOutlined />} suffix="U" />
          </Card>
        </Col>
        <Col xs={24} sm={12} xl={6}>
          <Card className="stats-card">
            <Statistic title="今日代理佣金" value={overview?.today_commission || 0} precision={2} prefix={<DollarOutlined />} suffix="U" />
          </Card>
        </Col>
      </Row>

      <Card
        title="代理列表"
        extra={overview?.today ? <Text type="secondary">统计日期：{formatDateText(overview.today)}</Text> : null}
      >
        <Table<AgentListItem>
          rowKey={(record) => record.agent.id}
          columns={columns}
          dataSource={agentItems}
          loading={isLoading}
          pagination={{
            current: pagination?.page || page,
            pageSize: pagination?.page_size || pageSize,
            total: pagination?.total || 0,
            showSizeChanger: true,
            onChange: (nextPage, nextPageSize) => {
              setPage(nextPage);
              setPageSize(nextPageSize || 10);
            },
          }}
          locale={{
            emptyText: <Empty description="暂无代理数据" />,
          }}
          scroll={{ x: 980 }}
        />
      </Card>

      <Modal
        title="添加代理"
        open={createVisible}
        onCancel={() => setCreateVisible(false)}
        onOk={handleCreateAgent}
        confirmLoading={createAgentMutation.isLoading}
        okText="确认添加"
        cancelText="取消"
      >
        <Form form={createForm} layout="vertical">
          <Form.Item
            label="用户标识"
            name="identifier"
            rules={[{ required: true, message: '请输入用户标识' }]}
            extra="支持 uid:123、tg:123456789，或直接输入纯数字。"
          >
            <Input placeholder="例如：uid:18 或 tg:7619699248" />
          </Form.Item>
        </Form>
      </Modal>

      <Drawer
        title="代理详情"
        width={760}
        open={selectedAgentId !== null}
        onClose={closeAgentDetail}
        destroyOnClose
      >
        {!agentDetail ? (
          <div style={{ padding: '48px 0' }}>
            {detailLoading ? <Text type="secondary">加载中...</Text> : <Empty description="暂无详情数据" />}
          </div>
        ) : (
          <Space direction="vertical" size={16} style={{ width: '100%' }}>
            <Descriptions bordered size="small" column={2}>
              <Descriptions.Item label="账号">{formatUserName(agentDetail.agent)}</Descriptions.Item>
              <Descriptions.Item label="邀请码">{agentDetail.agent.agent_code || '-'}</Descriptions.Item>
              <Descriptions.Item label="用户ID">{agentDetail.agent.id}</Descriptions.Item>
              <Descriptions.Item label="Telegram ID">{agentDetail.agent.telegram_id}</Descriptions.Item>
              <Descriptions.Item label="今日流水">
                {formatUsdt(agentDetail.today_stat?.turnover_amount)}
              </Descriptions.Item>
              <Descriptions.Item label="今日佣金">
                {formatUsdt(agentDetail.today_stat?.commission_amount)}
              </Descriptions.Item>
              <Descriptions.Item label="累计流水">
                {formatUsdt(agentDetail.lifetime_turnover)}
              </Descriptions.Item>
              <Descriptions.Item label="当前代理余额">
                {formatUsdt(agentDetail.agent_available_balance)}
              </Descriptions.Item>
              <Descriptions.Item label="代理可提现余额">
                {formatUsdt(agentDetail.agent_withdrawable_balance)}
              </Descriptions.Item>
              <Descriptions.Item label="代理待入账">
                {formatUsdt(agentDetail.agent_pending_balance)}
              </Descriptions.Item>
              <Descriptions.Item label="当前商户余额">
                {formatUsdt(agentDetail.merchant_available_balance)}
              </Descriptions.Item>
              <Descriptions.Item label="共享账户余额">
                {formatUsdt(agentDetail.agent.available_balance)}
              </Descriptions.Item>
              <Descriptions.Item label="下级商户数">{agentDetail.member_count || 0}</Descriptions.Item>
            </Descriptions>

            <Card size="small" title="邀请链接">
              <Space.Compact style={{ width: '100%' }}>
                <Input value={agentDetail.invite_link || ''} readOnly />
                <Button icon={<CopyOutlined />} onClick={() => handleCopy(agentDetail.invite_link)}>
                  复制
                </Button>
              </Space.Compact>
            </Card>

            <Card size="small" title="近 7 天流水">
              <Table<AgentStat>
                rowKey={(record) => record.stat_date}
                columns={recentStatColumns}
                dataSource={agentDetail.recent_stats || []}
                pagination={false}
                locale={{ emptyText: <Empty description="暂无统计数据" /> }}
                scroll={{ x: 640 }}
                size="small"
              />
            </Card>

            <Card
              size="small"
              title="今日流水明细"
              extra={agentDetail.turnover_page?.today ? (
                <Text type="secondary">统计日期：{formatDateText(agentDetail.turnover_page.today)}</Text>
              ) : null}
            >
              <Table<AgentTurnoverItem>
                rowKey={(record) => record.relation_id}
                columns={turnoverDetailColumns}
                dataSource={agentDetail.turnover_page?.items || []}
                pagination={{
                  current: agentDetail.turnover_page?.page || detailTurnoverPage,
                  pageSize: agentDetail.turnover_page?.page_size || detailTurnoverPageSize,
                  total: agentDetail.turnover_page?.total || 0,
                  showSizeChanger: true,
                  onChange: (nextPage, nextPageSize) => {
                    setDetailTurnoverPage(nextPage);
                    setDetailTurnoverPageSize(nextPageSize || 10);
                  },
                }}
                locale={{ emptyText: <Empty description="暂无流水明细" /> }}
                scroll={{ x: 520 }}
                size="small"
              />
            </Card>

            <Card size="small" title="下级商户">
              <Space direction="vertical" size={12} style={{ width: '100%' }}>
                <Text type="secondary">
                  这里只展示通过邀请链接归属、并已完成商户开通的账号。后台不再支持手动绑定已有账号。
                </Text>
                <Table<AgentMemberItem>
                  rowKey={(record) => record.relation_id}
                  columns={recentMemberColumns}
                  dataSource={agentDetail.recent_members || []}
                  pagination={false}
                  locale={{ emptyText: <Empty description="暂无下级数据" /> }}
                  scroll={{ x: 820 }}
                  size="small"
                />
              </Space>
            </Card>
          </Space>
        )}
      </Drawer>
    </div>
  );
};

export default AgentCenter;
